const WORD_TO_DIGIT = {
  zero: "0",
  oh: "0",
  one: "1",
  two: "2",
  three: "3",
  four: "4",
  five: "5",
  six: "6",
  seven: "7",
  eight: "8",
  nine: "9",
};


/** Collapse consecutive duplicate words/phrases from speech recognition overlap. */
export function dedupeTranscript(text) {
  if (!text) return "";
  let normalized = String(text).replace(/\s+/g, " ").trim();
  if (!normalized) return "";

  const words = normalized.split(" ");
  const dedupedWords = [];
  for (let i = 0; i < words.length; i += 1) {
    const current = words[i];
    const prev = dedupedWords[dedupedWords.length - 1];
    if (prev && prev.toLowerCase() === current.toLowerCase()) continue;
    dedupedWords.push(current);
  }

  let result = dedupedWords.join(" ");

  for (let size = 4; size >= 2; size -= 1) {
    const re = new RegExp(`(?:\\b(?:[\\w']+\\s+){${size - 1}}[\\w']+\\b)(?:\\s+\\1\\b)+`, "gi");
    result = result.replace(re, "$1");
  }

  return result.replace(/\s+/g, " ").trim();
}

function normalizeSpokenDigits(text) {
  if (!text) return "";
  const tokens = text.toLowerCase().replace(/[^\w\s]/g, " ").split(/\s+/).filter(Boolean);
  return tokens.map((t) => WORD_TO_DIGIT[t] ?? t).join("");
}

function extractMobile(text) {
  const normalized = normalizeSpokenDigits(text);
  const direct = normalized.match(/(?:\+91|91)?([6-9]\d{9})/);
  if (direct) return direct[1];

  const spaced = text.replace(/\D/g, "");
  const match = spaced.match(/([6-9]\d{9})/);
  return match ? match[1] : "";
}

function extractEmail(text) {
  const match = text.match(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/);
  return match ? match[0].toLowerCase() : "";
}

function extractPincode(text) {
  const normalized = normalizeSpokenDigits(text);
  const match = normalized.match(/\b(\d{6})\b/);
  return match ? match[1] : "";
}

function extractLabeledValue(text, labels) {
  const sorted = [...labels].sort((a, b) => b.length - a.length);
  const pattern = new RegExp(
    `\\b(?:${sorted.map((l) => l.replace(/\s+/g, "\\s+")).join("|")})\\b\\s*(?:is|:|-)?\\s*([^,.\\n;]+?)(?=\\s*,\\s*(?:customer|mobile|phone|email|business|company|address|city|pincode|note|notes|remark|source|enquiry)\\b|$)`,
    "i"
  );
  const match = text.match(pattern);
  return match ? match[1].trim() : "";
}

function extractCustomerType(text) {
  const lower = text.toLowerCase();
  if (/\bsalaried\b/.test(lower)) return "Salaried";
  if (/\bbusiness\b/.test(lower)) return "Business";
  return "";
}

function cleanValue(value) {
  let cleaned = dedupeTranscript(String(value || ""))
    .replace(/\s+/g, " ")
    .replace(/^[\s,.-]+|[\s,.-]+$/g, "")
    .trim();

  cleaned = cleaned.replace(
    /^(?:customer\s+name|name\s+is|owner\s+name|contact\s+name|person\s+name|mobile\s+number|phone\s+number|business\s+name|company\s+name|address\s+is|address|city\s+is|city|enquiry\s+source|source\s+is|note\s+is|notes?|remarks?)\s+/gi,
    ""
  );

  const words = cleaned.split(" ").filter(Boolean);
  while (words.length > 1 && words[0].toLowerCase() === words[1].toLowerCase()) {
    words.shift();
  }

  return words.join(" ").trim();
}

function buildNotesFromExplicitLabel(text) {
  const explicit = cleanValue(
    extractLabeledValue(text, [
      "note is",
      "notes are",
      "add note",
      "remark is",
      "remarks are",
      "additional note",
    ])
  );
  return explicit;
}

/**
 * Parse free-form voice transcript into enquiry form fields.
 */
export function parseEnquiryTranscript(rawTranscript) {
  const text = dedupeTranscript(String(rawTranscript || "").trim());
  if (!text) {
    return {
      customerName: "",
      mobile: "",
      email: "",
      businessName: "",
      address: "",
      city: "",
      pincode: "",
      notes: "",
      enquirySource: "",
      customerType: "",
      rawTranscript: "",
    };
  }

  const mobile = extractMobile(text);
  const email = extractEmail(text);
  const pincode = extractPincode(text);
  const customerType = extractCustomerType(text);

  const customerName = cleanValue(
    extractLabeledValue(text, [
      "customer name",
      "customer is",
      "owner name",
      "contact name",
      "person name",
      "name is",
    ])
  );

  const businessName = cleanValue(
    extractLabeledValue(text, [
      "business name",
      "company name",
      "company is",
      "business is",
      "firm name",
      "shop name",
    ])
  );

  const address = cleanValue(
    extractLabeledValue(text, ["address is", "located at", "location is", "address"])
  );

  const city = cleanValue(
    extractLabeledValue(text, ["city is", "from city", "in city", "city"])
  );

  const enquirySource = cleanValue(
    extractLabeledValue(text, [
      "enquiry source",
      "source is",
      "heard from",
      "referral from",
      "came from",
    ])
  );

  const notes = buildNotesFromExplicitLabel(text);

  return {
    customerName,
    mobile,
    email,
    businessName,
    address,
    city,
    pincode,
    notes,
    enquirySource,
    customerType,
    rawTranscript: text,
  };
}

/** Merge parsed voice fields into existing form state without wiping manual edits. */
export function mergeParsedEnquiryFields(current, parsed, { overwriteEmptyOnly = true } = {}) {
  const next = { ...current };
  const fields = [
    "customerName",
    "mobile",
    "email",
    "businessName",
    "address",
    "city",
    "pincode",
    "notes",
    "enquirySource",
    "customerType",
  ];

  fields.forEach((field) => {
    const value = parsed[field];
    if (!value) return;
    if (overwriteEmptyOnly && current[field]) return;
    next[field] = value;
  });

  return next;
}
